import React, { useState, useEffect } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import Navigation from './components/Navigation';
import BottomNav from './components/BottomNav';
import Footer from './components/Footer';
import LoadingSpinner from './components/LoadingSpinner';
import QuranReader from './pages/QuranReader';
import SurahList from './pages/SurahList';
import ReciterList from './pages/ReciterList';
import ReciterPlayer from './pages/ReciterPlayer';
import ReciterMusicLibrary from './pages/ReciterMusicLibrary';
import MealReader from './pages/MealReader';
import MealDetail from './pages/MealDetail';
import PrayerTimesPage from './pages/PrayerTimesPage';
import MaintenancePage from './pages/MaintenancePage';
import AdminLogin from './pages/admin/AdminLogin';
import AdminLayout from './pages/admin/AdminLayout';
import Dashboard from './pages/admin/Dashboard';
import SurahManagement from './pages/admin/SurahManagement';
import VerseManagement from './pages/admin/VerseManagement';
import BulkVerseUpload from './pages/admin/BulkVerseUpload';
import MealManagement from './pages/admin/MealManagement';
import ReciterManagement from './pages/admin/ReciterManagement';
import RecitationManagement from './pages/admin/RecitationManagement';
import TextManagement from './pages/admin/TextManagement';
import FooterSettings from './pages/admin/FooterSettings';
import SiteSettings from './pages/admin/SiteSettings';
import PrayerTimesSettings from './pages/admin/PrayerTimesSettings';
import MenuManagement from './pages/admin/MenuManagement';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Toaster } from './components/ui/toaster';
import { SEOProvider } from './context/SEOContext';
import SEOHead from './components/SEOHead';
import StructuredData from './components/StructuredData';

// IMPORTANT: REACT_APP_BACKEND_URL must be set in .env file
// In production (Emergent), this is automatically set during deployment
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!user) {
    return <Navigate to="/admin/login" replace />;
  }

  return children;
};

function AppContent() {
  const [loading, setLoading] = useState(true);
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [maintenanceMessage, setMaintenanceMessage] = useState('');
  const { user } = useAuth();
  const location = window.location.pathname;
  const isAdminRoute = location.startsWith('/admin');

  // Check maintenance mode on mount
  useEffect(() => {
    const checkMaintenance = async () => {
      // Admin users bypass maintenance check
      if (user && user.role === 'admin') {
        setMaintenanceMode(false);
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(`${BACKEND_URL}/api/site-settings`);
        if (response.ok) {
          const data = await response.json();
          if (data.maintenanceMode) {
            setMaintenanceMode(true);
            setMaintenanceMessage(data.maintenanceMessage || 'Sitemiz şu anda bakımdadır.');
          }
        }
      } catch (error) {
        console.error('Maintenance check error:', error);
      } finally {
        setLoading(false);
      }
    };

    checkMaintenance();
  }, [user]);

  useEffect(() => {
    // Simulate loading time only for non-admin routes
    if (!isAdminRoute) {
      const timer = setTimeout(() => {
        setLoading(false);
      }, 2000);
      return () => clearTimeout(timer);
    } else {
      setLoading(false);
    }
  }, [isAdminRoute]);

  if (loading && !isAdminRoute) {
    return <LoadingSpinner />;
  }

  // Show maintenance page for non-admin users
  if (maintenanceMode && !isAdminRoute && (!user || user.role !== 'admin')) {
    return <MaintenancePage message={maintenanceMessage} />;
  }

  return (
    <div className="App flex flex-col min-h-screen">
      <Routes>
        {/* Admin Routes */}
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin" element={
          <ProtectedRoute>
            <AdminLayout />
          </ProtectedRoute>
        }>
          <Route index element={<Navigate to="/admin/dashboard" replace />} />
          <Route path="dashboard" element={<Dashboard />} />
          <Route path="surahs" element={<SurahManagement />} />
          <Route path="surahs/:surahId/verses" element={<VerseManagement />} />
          <Route path="surahs/:surahId/bulk-upload" element={<BulkVerseUpload />} />
          <Route path="meals" element={<MealManagement />} />
          <Route path="reciters" element={<ReciterManagement />} />
          <Route path="recitations" element={<RecitationManagement />} />
          <Route path="text-management" element={<TextManagement />} />
          <Route path="footer-settings" element={<FooterSettings />} />
          <Route path="prayer-times" element={<PrayerTimesSettings />} />
          <Route path="menu-management" element={<MenuManagement />} />
          <Route path="settings" element={<SiteSettings />} />
        </Route>

        {/* Public Routes */}
        <Route
          path="*"
          element={
            <>
              <Navigation />
              <main className="flex-grow pb-16 md:pb-0">
                <Routes>
                  <Route path="/" element={<QuranReader />} />
                  <Route path="/sureler" element={<SurahList />} />
                  <Route path="/hafizlar" element={<ReciterList />} />
                  <Route path="/hafizlar/:reciterId" element={<ReciterMusicLibrary />} />
                  <Route path="/meal" element={<MealReader />} />
                  <Route path="/meal/:mealId" element={<MealDetail />} />
                  <Route path="/namaz-vakitleri" element={<PrayerTimesPage />} />
                </Routes>
              </main>
              <BottomNav />
              <Footer />
            </>
          }
        />
      </Routes>
      <Toaster />
    </div>
  );
}

function App() {
  return (
    <SEOProvider>
      <BrowserRouter>
        <SEOHead />
        <StructuredData />
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </BrowserRouter>
    </SEOProvider>
  );
}

export default App;